import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import axios from 'axios'

const API_BASE = process.env.REACT_APP_API_URL || '/api'
const MANUAL_PLAYER_OTP_ENABLED = process.env.REACT_APP_ENABLE_MANUAL_PLAYER_OTP === 'true'

const ManualOTP = () => {
  const [activeOTPs, setActiveOTPs] = useState([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [customOTP, setCustomOTP] = useState('')
  const [useCustomOTP, setUseCustomOTP] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef(null)

  const adminLogin = useSelector((state) => state.adminLogin || {})
  const { userInfo: adminInfo } = adminLogin

  const getAuthConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${adminInfo?.token}` },
  }), [adminInfo?.token])

  // Fetch active OTPs
  const fetchActiveOTPs = useCallback(async () => {
    if (!MANUAL_PLAYER_OTP_ENABLED || !adminInfo?.token) return
    setLoading(true)
    try {
      const { data } = await axios.get(`${API_BASE}/users/admin/active-otps`, getAuthConfig())
      if (data.success) {
        setActiveOTPs(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch active OTPs:', error)
    } finally {
      setLoading(false)
    }
  }, [adminInfo?.token, getAuthConfig])

  // Initial load and auto-refresh
  useEffect(() => {
    fetchActiveOTPs()
  }, [fetchActiveOTPs])

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchActiveOTPs, 5000) // every 5 seconds
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh, fetchActiveOTPs])

  // Generate manual OTP
  const handleGenerateOTP = async (e) => {
    e.preventDefault()
    if (!MANUAL_PLAYER_OTP_ENABLED) {
      toast.error('Player OTP management is currently disabled')
      return
    }

    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number')
      return
    }

    // Validate phone number (10 digits)
    const cleanPhone = phoneNumber.replace(/\D/g, '').slice(-10)
    if (cleanPhone.length !== 10 || !/^[6-9]/.test(cleanPhone)) {
      toast.error('Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9')
      return
    }

    if (useCustomOTP && customOTP && !/^\d{4}$/.test(customOTP)) {
      toast.error('Custom OTP must be exactly 4 digits')
      return
    }

    setGenerating(true)
    try {
      const payload = { phoneNumber: cleanPhone }
      if (useCustomOTP && customOTP) {
        payload.customOTP = customOTP
      }

      const { data } = await axios.post(
        `${API_BASE}/users/admin/generate-manual-otp`,
        payload,
        getAuthConfig()
      )

      if (data.success) {
        toast.success(`OTP ${data.data.otp} generated for ${data.data.phoneNumber}`)
        setPhoneNumber('')
        setCustomOTP('')
        setUseCustomOTP(false)
        fetchActiveOTPs()
      } else {
        toast.error(data.message || 'Failed to generate OTP')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate OTP')
    } finally {
      setGenerating(false)
    }
  }

  // Clear specific OTP
  const handleClearOTP = async (phone) => {
    if (!MANUAL_PLAYER_OTP_ENABLED) {
      toast.error('Player OTP management is currently disabled')
      return
    }

    try {
      const encoded = encodeURIComponent(phone)
      const { data } = await axios.delete(
        `${API_BASE}/users/admin/clear-otp/${encoded}`,
        getAuthConfig()
      )
      if (data.success) {
        toast.success('OTP cleared')
        fetchActiveOTPs()
      }
    } catch (error) {
      toast.error('Failed to clear OTP')
    }
  }

  // Format remaining time
  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Expired'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Get channel badge color
  const getChannelColor = (channel) => {
    switch (channel) {
      case 'manual': return { bg: 'var(--cm-success-soft)', color: 'var(--cm-success)', label: 'Manual' }
      case 'sms': return { bg: 'var(--cm-info-soft)', color: 'var(--cm-info)', label: 'SMS' }
      case 'whatsapp': return { bg: 'var(--cm-success-soft)', color: 'var(--cm-success)', label: 'WhatsApp' }
      default: return { bg: 'var(--cm-surface-2)', color: 'var(--cm-text-muted)', label: channel || 'System' }
    }
  }

  if (!MANUAL_PLAYER_OTP_ENABLED) {
    return (
      <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{
          background: 'var(--cm-warning-soft)',
          borderRadius: '16px',
          padding: '28px',
          border: '1px solid var(--cm-border)',
          boxShadow: 'none'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--cm-text)', margin: 0 }}>
            Player OTP Management
          </h1>
          <p style={{ color: 'var(--cm-text)', margin: '10px 0 0', fontSize: '15px', lineHeight: 1.6 }}>
            This screen is hidden while player login runs on the default app OTP fallback.
            Re-enable manual player OTP management after the SMS or WhatsApp provider keys are configured.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '24px', flexWrap: 'wrap', gap: '12px'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--cm-text)', margin: 0 }}>
            Player OTP Management
          </h1>
          <p style={{ color: 'var(--cm-text-muted)', margin: '4px 0 0', fontSize: '14px' }}>
            Generate and manage OTPs for player login
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', color: 'var(--cm-text-muted)', cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-refresh
          </label>
          <button
            onClick={fetchActiveOTPs}
            disabled={loading}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--cm-border)',
              background: 'var(--cm-surface)', cursor: 'pointer', fontSize: '13px',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Generate OTP Form */}
      <div style={{
        background: 'var(--cm-surface)', borderRadius: '12px', padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px',
        border: '1px solid var(--cm-border)'
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: 'var(--cm-text)' }}>
          Generate OTP for Player
        </h2>
        <form onSubmit={handleGenerateOTP}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cm-text-muted)', marginBottom: '6px' }}>
                Player Mobile Number
              </label>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                placeholder="Enter 10-digit mobile number"
                maxLength={10}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '8px',
                  border: '1px solid var(--cm-border)', fontSize: '15px', outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {useCustomOTP && (
              <div style={{ flex: '0 0 160px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cm-text-muted)', marginBottom: '6px' }}>
                  Custom OTP
                </label>
                <input
                  type="text"
                  value={customOTP}
                  onChange={(e) => setCustomOTP(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
                  placeholder="4 digits"
                  maxLength={4}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid var(--cm-border)', fontSize: '15px', outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '13px', color: 'var(--cm-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap'
              }}>
                <input
                  type="checkbox"
                  checked={useCustomOTP}
                  onChange={(e) => {
                    setUseCustomOTP(e.target.checked)
                    if (!e.target.checked) setCustomOTP('')
                  }}
                  style={{ cursor: 'pointer' }}
                />
                Set custom OTP
              </label>
            </div>

            <button
              type="submit"
              disabled={generating || !phoneNumber.trim()}
              style={{
                padding: '10px 24px', borderRadius: '8px', border: 'none',
                background: generating || !phoneNumber.trim() ? 'var(--cm-surface-3)' : 'var(--cm-gold)',
                color: generating || !phoneNumber.trim() ? 'var(--cm-text-muted)' : 'var(--cm-gold-ink)', cursor: generating || !phoneNumber.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap'
              }}
            >
              {generating ? 'Generating...' : 'Generate OTP'}
            </button>
          </div>
        </form>
      </div>

      {/* Active OTPs Table */}
      <div style={{
        background: 'var(--cm-surface)', borderRadius: '12px', padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid var(--cm-border)'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '16px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cm-text)', margin: 0 }}>
            Active OTPs
            <span style={{
              background: 'var(--cm-info-soft)', color: 'var(--cm-info)', fontSize: '13px',
              padding: '2px 10px', borderRadius: '12px', marginLeft: '10px',
              fontWeight: '500'
            }}>
              {activeOTPs.length}
            </span>
          </h2>
        </div>

        {activeOTPs.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '48px 24px', color: 'var(--cm-text-muted)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--cm-text-faint)" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <p style={{ fontSize: '15px', fontWeight: '500' }}>No active OTPs</p>
            <p style={{ fontSize: '13px' }}>Generate an OTP above when a player requests login</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--cm-border)' }}>
                  {['Player', 'Phone Number', 'OTP', 'Source', 'Time Left', 'Attempts', 'Generated By', 'Actions'].map((h) => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left', fontSize: '12px',
                      fontWeight: '600', color: 'var(--cm-text-muted)', textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeOTPs.map((otp, idx) => {
                  const channelInfo = getChannelColor(otp.channel)
                  const isExpiring = otp.remainingSeconds <= 60
                  return (
                    <tr key={otp.phoneNumber + idx} style={{
                      borderBottom: '1px solid var(--cm-surface-2)',
                      background: isExpiring ? 'var(--cm-warning-soft)' : 'transparent'
                    }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--cm-text)' }}>
                          {otp.playerName}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--cm-text-muted)' }}>
                          {otp.playerIdentifier}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: 'var(--cm-text)' }}>
                        {otp.phoneNumber}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          fontFamily: 'monospace', fontSize: '22px', fontWeight: '700',
                          color: 'var(--cm-gold-text)', letterSpacing: '3px',
                          background: 'var(--cm-warning-soft)', padding: '6px 14px', borderRadius: '8px',
                          display: 'inline-block', border: '1px solid var(--cm-border)'
                        }}>
                          {otp.otp}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          background: channelInfo.bg, color: channelInfo.color,
                          padding: '4px 10px', borderRadius: '6px', fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {channelInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          fontWeight: '600', fontSize: '14px',
                          color: isExpiring ? 'var(--cm-warning)' : 'var(--cm-success)'
                        }}>
                          {formatTime(otp.remainingSeconds)}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          background: otp.attempts > 0 ? 'var(--cm-danger-soft)' : 'var(--cm-surface-2)',
                          color: otp.attempts > 0 ? 'var(--cm-danger)' : 'var(--cm-text-muted)',
                          padding: '4px 10px', borderRadius: '6px', fontSize: '13px',
                          fontWeight: '500'
                        }}>
                          {otp.attempts}/{otp.channel === 'manual' ? 1 : 3}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--cm-text-muted)' }}>
                        {otp.generatedBy}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          onClick={() => handleClearOTP(otp.phoneNumber)}
                          style={{
                            padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--cm-border)',
                            background: 'var(--cm-surface)', color: 'var(--cm-danger)', cursor: 'pointer',
                            fontSize: '12px', fontWeight: '500'
                          }}
                        >
                          Clear
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div style={{
        marginTop: '16px', padding: '14px 18px', background: 'var(--cm-surface-2)',
        borderRadius: '8px', fontSize: '13px', color: 'var(--cm-text-muted)',
        border: '1px solid var(--cm-border)'
      }}>
        <strong>How it works:</strong> When a player enters their mobile number on the login screen,
        generate their OTP here. Share the OTP verbally with the player. They enter it on their
        login screen to complete authentication. OTPs expire after 1 minute and are single-use only.
      </div>
    </div>
  )
}

export default ManualOTP
